import React from "react";
import {
  useProcedureById,
  useProjectById,
  useUnitById,
} from "../../../../common/CommonQueries";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  makeStyles,
  Theme,
} from "@material-ui/core";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import { TravelerModal } from "../../../../common/travelerModal";
import { Link } from "react-router-dom";
import { useDispositionsByName } from "../../../../common/services/dispositionServices";
import { ErrorMessage } from "../../../../common/ErrorMessage";
import { ELImageReviewModal } from "../../../../dataEgress/ELImageReviewModal";
import { IVReviewModal } from "../../../../dataEgress/IVReview/IVReviewModal";
import { LeakedDataModal } from "../../../../dataEgress/LeakedDataModal";
import { VisualInspectionReviewModal } from "../../../../dataEgress/VisualInspectionReviewModal";
import { DiodeTestModal } from "../../../../dataEgress/DiodeTestModal";
import { ColorimeterReviewModal } from "../../../../dataEgress/ColorimeterModal";
import { StressDataModal } from "../../../../dataEgress/StressDataModal";

const useStyles = makeStyles((theme: Theme) => ({
  attachedContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    width: "100%",
  },
  attachedChip: {
    backgroundColor: "#fff6e5",
    color: "#d97706",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 8,
    border: "1px solid #fde68a",
    padding: "4px 6px",
    height: "auto",
    "& .MuiChip-label": {
      paddingLeft: 6,
      paddingRight: 10,
      paddingTop: 2,
      paddingBottom: 2,
    },
    "& .MuiChip-icon": {
      color: "#d97706",
      fontSize: 18,
      marginLeft: 8,
    },
  },
  projectLink: {
    textDecoration: "none",
  },
}));

const RenderUnit = ({ item }: any) => {
  const classes = useStyles();
  const {
    data: unit,
    isLoading: isLoadingUnit,
  } = useUnitById(item.id);

  if (isLoadingUnit) {
    return <CircularProgress size={16} style={{ color: "#d97706" }} />;
  }

  const serial = unit?.serial_number || item.str || item.id;

  return (
    <TravelerModal
      serialNumber={serial}
      customTrigger={
        <Chip
          icon={<DescriptionOutlinedIcon />}
          label={serial}
          className={classes.attachedChip}
          clickable
        />
      }
    />
  );
};

const RenderProject = ({ item }: any) => {
  const classes = useStyles();
  const {
    data: project,
    isLoading: isLoadingProject,
  } = useProjectById(item.id);

  if (isLoadingProject) {
    return <CircularProgress size={16} style={{ color: "#d97706" }} />;
  }

  const projectNumber = project?.number || item.str || `Project #${item.id}`;

  return (
    <Link
      to={`/project_management/project_intelligence/${project?.id || item.id}`}
      className={classes.projectLink}
    >
      <Chip
        icon={<FolderOutlinedIcon />}
        label={projectNumber}
        className={classes.attachedChip}
        clickable
      />
    </Link>
  );
};

const RenderResult = ({ item }: any) => {
  const classes = useStyles();
  const {
    data: procedure,
    isError: isErrorProcedure,
    isLoading: isLoadingProcedure,
    error: errorProcedure,
  } = useProcedureById(item.id);

  const {
    data: dispositionPassed,
    isLoading: isLoadingDispositionPassed,
    isError: isErrorDispositionPassed,
    error: errorDispositionPassed,
  } = useDispositionsByName("Pass");

  if (isLoadingProcedure || isLoadingDispositionPassed) {
    return <CircularProgress size={16} style={{ color: "#d97706" }} />;
  }

  if (isErrorProcedure || isErrorDispositionPassed) {
    return (
      <>
        {isErrorProcedure && <ErrorMessage error={errorProcedure} />}
        {isErrorDispositionPassed && (
          <ErrorMessage error={errorDispositionPassed} />
        )}
      </>
    );
  }

  switch (procedure?.visualizer) {
    case "el_image":
      return (
        <ELImageReviewModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "iv_curve":
      return (
        <IVReviewModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "wet_leakage":
      return (
        <LeakedDataModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "visual_inspection":
      return (
        <VisualInspectionReviewModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "diode":
      return (
        <DiodeTestModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "colorimeter":
      return (
        <ColorimeterReviewModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          dispositionPassedId={dispositionPassed?.id}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    case "stress":
      return (
        <StressDataModal
          id={procedure.id}
          disposition={null}
          name={procedure.procedure_definition_name}
          open_notes={procedure.open_notes}
          has_notes={procedure.has_notes}
        />
      );
    default:
      return (
        <Chip
          icon={<AssignmentOutlinedIcon />}
          label={procedure?.procedure_definition_name || item.str || "Procedure"}
          className={classes.attachedChip}
        />
      );
  }
};

interface AttachedItem {
  model_name?: string;
  id?: number;
  str?: string;
  [key: string]: any;
}

interface NotesAttachedProps {
  attached?: AttachedItem[];
  isNew?: boolean;
}

export const NotesAttached: React.FC<NotesAttachedProps> = ({ attached }) => {
  const classes = useStyles();

  const renderAttachedItem = (item: AttachedItem) => {
    switch (item.model_name) {
      case "unit":
        return <RenderUnit item={item} />;
      case "project":
        return <RenderProject item={item} />;
      case "procedureresult":
        return <RenderResult item={item} />;
      default:
        return (
          <Chip
            icon={<DescriptionOutlinedIcon />}
            label={item.str || item.id}
            className={classes.attachedChip}
          />
        );
    }
  };

  return (
    <Box className={classes.attachedContainer}>
      {attached?.map((item: AttachedItem, index: number) => (
        <Box key={item.id || index}>{renderAttachedItem(item)}</Box>
      ))}
    </Box>
  );
};

export const ThreadedAttached = NotesAttached;